import api from 'api/apiClient';
import { formatDate } from 'modules/shared/utils';
import { ensureFileId } from 'modules/shared/service';
import { API_LEAVE_STATUS_KEYS } from '../constants';
import { calculateLeaveDuration } from '../utils';

const unwrap = async (promise) => {
  const res = await promise;
  return res.data;
};

export async function getAdminLeaves() {
  const { data: raw } = await unwrap(await api.get('/api/hr/leave-requests'));

  const normalized = {
    pending: [],
    approved: [],
    rejected: [],
  };

  raw.forEach((leave) => {
    const uiStatus = API_LEAVE_STATUS_KEYS[leave.status];
    if (!uiStatus) return;

    normalized[uiStatus].push({
      id: leave.id,
      employeeId: leave.employee_id,
      employeeName: leave.employee_name,
      department: leave.department,
      leaveType: leave.leave_type,
      startDate: formatDate(leave.start_date),
      endDate: leave.end_date ? formatDate(leave.end_date) : null,
      startSession: leave.start_date_session,
      endSession: leave.end_date_session ?? null,
      numberOfDays: Number(leave.number_of_days),
      isPaid: Boolean(leave.is_paid),
      appliedOn: formatDate(leave.applied_on),
      reason: leave.reason,
      status: uiStatus,
      rejectionReason: leave.rejection_reason ?? null,
    });
  });

  return normalized;
}

export async function approveLeave(id, adminId) {
  if (!id) throw new Error('Leave id is required for approval');

  await unwrap(
    await api.put('/api/hr/leave-update', {
      leave_id: id,
      status: 'Accepted',
      handled_by: adminId,
    }),
  );
}

export async function rejectLeave(id, reason, adminId) {
  if (!id) throw new Error('Leave id is required for rejection');

  await unwrap(
    await api.put('/api/hr/leave-update', {
      leave_id: id,
      status: 'Rejected',
      rejection_reason: reason,
      handled_by: adminId,
    }),
  );
}

export async function getEmployeesList() {
  const { data: raw } = await unwrap(await api.get('/api/hr/employees'));
  const activeEmployees = raw.filter((emp) => emp.employment_period?.includes('Present'));

  return activeEmployees.map((emp) => ({
    id: emp.id,
    employeeId: emp.employee_id,
    name: emp.name,
    department: emp.department,
    designation: emp.designation,
  }));
}

export async function applyLeaveForEmployee(data, adminId) {
  if (!data) throw new Error('Leave data is required for application');

  const duration = calculateLeaveDuration({
    startDate: data.startDate,
    startSession: data.startSession,
    endDate: data.endDate,
    endSession: data.endSession,
  });

  const payload = {
    employee_id: data.employeeId,
    leave_type: data.leaveType,
    start_date: formatDate(data.startDate, 'YYYY-MM-DD'),
    end_date: data.endDate ? formatDate(data.endDate, 'YYYY-MM-DD') : null,
    start_date_session: data.startSession,
    end_date_session: data.endSession ? data.endSession : null,
    number_of_days: duration,
    reason: data.reason,
    attachment: await ensureFileId(data.attachment),
    applied_on: new Date().toISOString(),
    status: 'Accepted',
    handled_by: adminId,
  };

  await unwrap(await api.post('/api/hr/leave-request/admin-apply', payload));
}
